<template lang="pug">
  #team-steps-chart
    WtTitle(card-title) Team Members Step Data Over Time
    WtBetweenDatesInput(
      :fetch-action="fetchAction"
      @newData="data = $event; currentPage = data.pagination.currentPage"
      :initDates="dates"
      @newDates="dates = $event"
    )
    HorizontalBarChart(
      v-if="loadedStatus && !_.isEmpty(data)"
      :chart-data="chartData"
      :show-legend="true"
      @legendClicked="legendClicked"
    )
    WtPagination(
      :paginationObject="paginationObject"
      @currentPage="currentPage = $event"
      order="is-centered"
    )
    .empty-state(v-if="_.isEmpty(data)")
      p Hey there,
      br
      p A chart will show here when data is available.
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import WtTitle from "@@/WtTitle/WtTitle";
import WtPagination from "@@/WtPagination/WtPagination";
import WtBetweenDatesInput from "@@/WtBetweenDatesInput/WtBetweenDatesInput";
import HorizontalBarChart from "@/charts/HorizontalBarChart";

export default {
  name: "TeamMembersStepsChart",

  components: {
    HorizontalBarChart,
    WtTitle,
    WtPagination,
    WtBetweenDatesInput
  },

  props: {
    team: {
      type: Object,
      default: () => {
        return {
          id: null,
          name: null,
          description: null,
          public: null,
          inviteOnly: null,
          logo: null,
          maxMembers: null,
          numMembers: null,
          initiationDate: null,
          isStepsHidden: false,
          isActivitiesHidden: false
        };
      }
    }
  },

  data() {
    return {
      data: null,
      dates: [this.moment().subtract(7, "days")._d, this.moment()._d],
      loadedStatus: false,
      startDate: null,
      endDate: null,
      currentPage: 1
    };
  },

  computed: {
    ...mapGetters("program", ["coreProgramMeta"]),

    paginationObject() {
      return this._.get(this.data, "pagination", null);
    },

    chartData() {
      const data = this.data.data
        .map(member => {
          let steps = member.stepData.reduce(
            (acc, curr) => acc + curr.steps,
            0
          );
          let activities = member.stepData.reduce(
            (acc, curr) => acc + curr.activities,
            0
          );
          let totalStepsAndActivities = steps + activities;
          return {
            username: member.username,
            steps,
            activities,
            totalStepsAndActivities
          };
        })
        .sort((memberA, memberB) => {
          if (this.isStepsHidden) {
            return memberB.activities - memberA.activities;
          }
          if (this.isActivitiesHidden) {
            return memberB.steps - memberA.steps;
          }
          return (
            memberB.totalStepsAndActivities - memberA.totalStepsAndActivities
          );
        });

      let dataSteps = data.map(member => member.steps);
      let dataActivities = data.map(member => member.activities);
      let labels = data.map(member => member.username);

      const datasets = [
        {
          label: "Steps",
          backgroundColor: this._app_chart_two_color.steps,
          barPercentage: 0.6,
          data: dataSteps
        }
      ];
      // Only Show Activities if the ActivityConverter is enabled.
      if (this.coreProgramMeta.converter) {
        datasets.push({
          label: "Activities",
          backgroundColor: this._app_chart_two_color.activities,
          barPercentage: 0.6,
          data: dataActivities
        });
      }

      return { labels, datasets };
    }
  },

  watch: {
    async currentPage() {
      this.loadedStatus = true;
      this.data = await this.fetchTeamMembersStepsData({
        teamId: this.team.id,
        startDate: this.dates[0],
        endDate: this.dates[1],
        page: this.currentPage
      });
      this.loadedStatus = true;
    }
  },

  async mounted() {
    this.data = await this.fetchTeamMembersStepsData({
      teamId: this.team.id,
      startDate: this.dates[0],
      endDate: this.dates[1]
    });
    this.loadedStatus = true;
  },

  methods: {
    ...mapActions("teams", ["fetchTeamMembersStepsData"]),

    async fetchAction(params) {
      const response = await this.fetchTeamMembersStepsData({
        teamId: this.team.id,
        startDate: params.startDate,
        endDate: params.endDate,
        page: this.currentPage
      });

      return response;
    },

    legendClicked(event) {
      if (event.index === 0) {
        if (event.isHidden) {
          this.isStepsHidden = true;
        } else {
          this.isStepsHidden = false;
        }
      }
      if (event.index === 1) {
        if (event.isHidden) {
          this.isActivitiesHidden = true;
        } else {
          this.isActivitiesHidden = false;
        }
      }
    }
  }
};
</script>

<style lang="sass" scoped>
#team-steps-chart
  min-height: 200px
</style>
